import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import snakecaseKeys from 'snakecase-keys';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

type IdentifierForSupplierResponse = {
  code: string;
  id: UUID;
  message: string;
};

type IdentifierForSupplierData = {
  address: Address;
  blacklisted?: boolean;
  identifier: string;
  identifyingId: UUID;
  ignoreAddressInfo?: boolean;
  supplierId: UUID;
  updateDlns?: boolean;
};

type IdentifierForSupplierArguments = {
  siteData: IdentifierForSupplierData;
  siteId: UUID;
};

type IdentifierForSupplierOptions = UseMutationOptions<
  IdentifierForSupplierResponse,
  Error,
  IdentifierForSupplierArguments
>;

/**
 * Adds a new site identifier for a supplier's site.
 * This allows adding a blacklisted site identifier.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/add_identifier_for_supplier_site__site_uuid__identifier_post
 *
 * @param {UUID} siteId - The UUID of the site.
 * @param {IdentifierForSupplierData} siteData - Data related to the site identifier.
 * @returns {Promise<IdentifierForSupplierResponse>} The response from the API after the POST request.
 */
export const addIdentifierForSupplier = async (
  siteId: UUID,
  siteData: IdentifierForSupplierData,
) => {
  const { updateDlns, ...other } = siteData;
  const body = withoutObjectKeysWhereValueIs(
    {
      ignoreAddressInfo: false,
      blacklisted: false,
      ...other,
    },
    [undefined],
  );
  const searchParams = withoutObjectKeysWhereValueIs(
    {
      updateDlns,
    },
    [undefined],
  );

  const response = await vestigasApi
    .post(ENDPOINT.SITE.IDENTIFIER_FOR_SUPPLIER(siteId), {
      json: body,
      searchParams: snakecaseKeys(searchParams),
    })
    .json<IdentifierForSupplierResponse>();

  return response;
};

/**
 * Custom hook for adding a site identifier for a supplier.
 *
 * @param {Object} options - Additional options for the mutation.
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationAddIdentifierForSupplier = (
  options?: IdentifierForSupplierOptions,
) =>
  useMutation({
    mutationFn: async ({ siteId, siteData }: IdentifierForSupplierArguments) =>
      addIdentifierForSupplier(siteId, siteData),
    ...options,
  });
